import { Components } from "@thisgorilla/primate-ds";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useImageContentContext } from ".";
import {
  DEFAULT_IMAGE_HEIGHT,
  DEFAULT_IMAGE_TRANSITION_DURATION,
} from "../../../../../../../utils";
import { usePostCardContext } from "../../PostCardContent";
import { ExpandedPostCarousel } from "./ExpandedPostCarousel";
import { PostCarousel } from "./PostCarousel";
import { ImageContentProps } from "./imageContent";

export const WithCarousel = () => {
  const { isOnExpandedPostView } = usePostCardContext();
  const { carouselIndex, Images, setCarouselIndex } = useImageContentContext();
  const [containerHeight, setContainerHeight] = useState(DEFAULT_IMAGE_HEIGHT);
  const [scale, setScale] = useState<ImageContentProps.Scale>(1);
  const [isRefReady, setIsRefReady] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) setIsRefReady(true);
  }, []);
  useResizeObserver(
    isRefReady ? containerRef.current : null,
    carouselIndex,
    setContainerHeight
  );

  const handleClick = (e: any, upcomingIndex: number) => {
    e.stopPropagation();
    setCarouselIndex(upcomingIndex);
    setScale(1);
    if (!containerRef.current) return;
    const currentImage = containerRef.current.children[carouselIndex];
    if (!currentImage) return;
    const upcomingImage = containerRef.current.children[upcomingIndex];
    if (!upcomingImage) return;

    const upcomingImageHeight = upcomingImage.getBoundingClientRect().height;
    const currentImageHeight = currentImage.getBoundingClientRect().height;
    if (upcomingImageHeight < 250) return;

    const scale = upcomingImageHeight - currentImageHeight > 0 ? 1.05 : 0.95;
    setScale(scale);
  };
  const handleNextClick = (e: any) => handleClick(e, carouselIndex + 1);
  const handlePrevClick = (e: any) => handleClick(e, carouselIndex - 1);

  return (
    <>
      <ImageContainer ref={containerRef} style={{ height: containerHeight }}>
        {Images.map((Image, index) => (
          <PreloadedImage
            key={index}
            $isVisible={index === carouselIndex}
            data-visible={index === carouselIndex}
            $scale={scale}
            $isOnExpandedPostView={isOnExpandedPostView}
          >
            {Image}
          </PreloadedImage>
        ))}
      </ImageContainer>
      <StepperContainer
        className="stepper-container"
        $isOnExpandedPostView={isOnExpandedPostView}
      >
        <Components.Stepper
          steps={Images.length}
          selectedStep={carouselIndex + 1}
          view="Gallery only"
          onColor="On Transparent"
        />
      </StepperContainer>
      <CarouselContainer className="carousel-container">
        {isOnExpandedPostView ? (
          <ExpandedPostCarousel
            {...{
              onNextClick: handleNextClick,
              onPrevClick: handlePrevClick,
            }}
          />
        ) : (
          <PostCarousel
            {...{
              onNextClick: handleNextClick,
              onPrevClick: handlePrevClick,
            }}
          />
        )}
      </CarouselContainer>
    </>
  );
};

//#region  Styles
const ImageContainer = styled.div`
  position: relative;
  transition: height ${DEFAULT_IMAGE_TRANSITION_DURATION}ms ease-in-out;
  overflow: hidden;
  min-height: ${DEFAULT_IMAGE_HEIGHT}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const PreloadedImage = styled.div<{
  $isVisible: boolean;
  $scale: number;
  $isOnExpandedPostView: boolean;
}>(({ $isVisible, $scale, $isOnExpandedPostView }) => {
  const transformStyle = `transform: ${
    $isVisible
      ? "scale(1)"
      : `scale(${$scale});
    transition: transform ${DEFAULT_IMAGE_TRANSITION_DURATION}ms ease-in-out`
  };`;

  return `
  opacity: ${$isVisible ? 1 : $isOnExpandedPostView ? 1 : 0};
  visibility: ${$isVisible ? "visible" : "hidden"};
  position: ${$isVisible ? "relative" : "absolute"};
  top: 0;
  left: 0;
  width: 100%;
  transition: opacity ${DEFAULT_IMAGE_TRANSITION_DURATION}ms ease-in-out;
  ${transformStyle}
`;
});
const StepperContainer = styled.div<{ $isOnExpandedPostView: boolean }>(
  ({ $isOnExpandedPostView }) => `
  position: absolute;
  top: 0;
  left: 0%;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: ${$isOnExpandedPostView ? "end" : "start"};
`
);
const CarouselContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;
//#endregion

function useResizeObserver(
  parentElement: Element | null,
  index: number,
  onResize: Dispatch<SetStateAction<number>>
) {
  useEffect(() => {
    if (!parentElement) return;

    const visibleImage = parentElement.children[index] as Element | null;
    if (!visibleImage) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        if (height > 0) onResize(height);
      }
    });

    resizeObserver.observe(visibleImage);
    return () => resizeObserver.disconnect();
  }, [index, onResize, parentElement]);
}
